// Magic-link verification screen (route /verify/:token). On mount it consumes
// the token. Five states drive the UI:
//   - verifying : role="status" live region while the request is in flight.
//   - success   : confirmation + auto-redirect to /intake (with a manual
//                 "Continue" fallback) and a cross-tab broadcast.
//   - expired / used / invalid : role="alert" with recovery options. We never
//     leak why a token failed beyond these benign buckets.
import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Container, Paper, Typography, Button, CircularProgress, Alert, Stack,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import { verifyMagicLink, resendMagicLink, resetContact } from '../store/contact-slice.js';
import ThemeModeToggle from '../components/ThemeModeToggle.jsx';
import SanbornLogo from '../components/SanbornLogo.jsx';

const RECOVERY_COPY = {
  expired: 'That link has expired. Verification links are valid for 30 minutes.',
  used: 'That link has already been used. If it wasn’t you, request a fresh one.',
  invalid: 'That link is no longer valid. Request a new one to continue.',
};

const VerifyMagicLinkPage = () => {
  const { token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    verified, verifyError, contact,
  } = useSelector((s) => s.contact);

  const headingRef = useRef(null);

  useEffect(() => {
    if (token) dispatch(verifyMagicLink(token));
  }, [token, dispatch]);

  useEffect(() => { headingRef.current?.focus(); }, [verified, verifyError]);

  // On success: tell other tabs, then auto-advance to the intake form.
  useEffect(() => {
    if (!verified) return undefined;
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('intake-verification');
      channel.postMessage('verified');
      channel.close();
    }
    const id = setTimeout(() => navigate('/intake', { replace: true }), 1500);
    return () => clearTimeout(id);
  }, [verified, navigate]);

  const handleResend = () => {
    if (!contact) {
      dispatch(resetContact());
      navigate('/', { replace: true });
      return;
    }
    dispatch(resendMagicLink(contact));
    navigate('/check-email', { replace: true });
  };

  const handleStartOver = () => {
    dispatch(resetContact());
    navigate('/', { replace: true });
  };

  const renderBody = () => {
    if (verified) {
      return (
        <Stack spacing={2} sx={{ textAlign: 'center' }}>
          <Box><CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} /></Box>
          <Typography ref={headingRef} tabIndex={-1} variant="h4" component="h1" sx={{ fontWeight: 700, outline: 'none' }}>
            You&apos;re verified
          </Typography>
          <Typography color="text.secondary">
            Taking you to your imagery request…
          </Typography>
          <Box>
            <Button variant="contained" onClick={() => navigate('/intake', { replace: true })}>
              Continue
            </Button>
          </Box>
        </Stack>
      );
    }

    if (verifyError) {
      return (
        <Stack spacing={2}>
          <Typography ref={headingRef} tabIndex={-1} variant="h4" component="h1" sx={{ fontWeight: 700, outline: 'none', textAlign: 'center' }}>
            Let&apos;s try that again
          </Typography>
          <Alert severity="warning" role="alert">{RECOVERY_COPY[verifyError] || RECOVERY_COPY.invalid}</Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            <Button variant="contained" onClick={handleResend}>
              {contact ? 'Send me a new link' : 'Start over'}
            </Button>
            {contact && (
              <Button variant="text" onClick={handleStartOver}>Use a different email</Button>
            )}
          </Stack>
        </Stack>
      );
    }

    // Verifying (default).
    return (
      <Stack spacing={2} sx={{ textAlign: 'center' }} role="status" aria-live="polite">
        <Box><CircularProgress /></Box>
        <Typography ref={headingRef} tabIndex={-1} variant="h5" component="h1" sx={{ fontWeight: 700, outline: 'none' }}>
          Verifying your email…
        </Typography>
        <Typography color="text.secondary">This only takes a moment.</Typography>
      </Stack>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 12, right: 16 }}><ThemeModeToggle /></Box>
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ color: 'text.primary', mb: 3, textAlign: 'center' }}>
          <SanbornLogo height={72} href="https://sanborn.com/" />
        </Box>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            borderTop: 3,
            borderTopColor: 'primary.main',
          }}
        >
          {renderBody()}
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyMagicLinkPage;
